import { useRacingEventCompetitors } from "@/hooks/firestore/betting/useRacingEvents";
import { useEvent } from "@/hooks/firestore/betting/useEvent";
import { useMemo } from "react";
import type { Always, NullableMaybe } from "@/types/utilities";
import {
  useControlsStoreSelector,
  type ControlsStore,
} from "@/features/race/stores/controlsStore";

const compare = (
  a: number,
  b: number,
  order: ControlsStore["sortDirection"],
) => {
  const dir = order === "asc" ? 1 : -1;

  return (a - b) * dir;
};

const parseBarrier = (value?: string | number) => {
  if (!value) return { row: 99, num: 999 };

  const str = String(value);

  // Thoroughbred: numeric barrier
  if (!isNaN(Number(str))) {
    return { row: 0, num: Number(str) };
  }

  // Harness: Fr1, Sr2, etc.
  const match = str.match(/(Fr|Sr)(\d+)/);
  if (!match) return { row: 99, num: 999 };

  const row = match[1] === "Fr" ? 1 : 2;
  const num = Number(match[2]);

  return { row, num };
};

const useSortedCompetitors = (
  eventId: NullableMaybe<string>,
  onlyScratchings = false,
  isPickems = false,
) => {
  const { competitors, isLoading: competitorsLoading } =
    useRacingEventCompetitors(eventId);

  const { event, isLoading: marketsLoading } = useEvent(eventId, "all");

  const markets = event?.markets || [];

  const winnerMarket = (markets || []).find(
    (market) => market.marketType === "RACING_WINNER",
  );

  const sortBy = useControlsStoreSelector((state) => state.sortBy, isPickems);
  const sortDirection = useControlsStoreSelector(
    (state) => state.sortDirection,
    isPickems,
  );

  const sortedCompetitors = useMemo(() => {
    if (!competitors || !markets) return;

    return competitors
      .filter((c) => c.visible && (!onlyScratchings || c.scratched))
      .sort((a, b) => {
        // push scratched competitors to the bottom
        if (a.scratched !== b.scratched)
          return compare(Number(a.scratched), Number(b.scratched), "asc");

        if (sortBy === "runner") {
          return compare(a.number, b.number, sortDirection);
        }

        if (sortBy === "barrier") {
          const getBarrier = (c: typeof a) => {
            if (event?.sport === "HARNESS_RACING") {
              const hasAlphaBarrier =
                typeof c.hcpDraw === "string" && /[A-Za-z]/.test(c.hcpDraw);
              return hasAlphaBarrier ? c.hcpDraw : c.startingPosition;
            }

            if (event?.sport === "HORSE_RACING") {
              return c.startingPosition;
            }

            return undefined;
          };

          const barrierA = parseBarrier(getBarrier(a));
          const barrierB = parseBarrier(getBarrier(b));

          const dir = sortDirection === "asc" ? 1 : -1;

          if (barrierA.row !== barrierB.row) {
            return (barrierA.row - barrierB.row) * dir;
          }

          return (barrierA.num - barrierB.num) * dir;
        }

        if (sortBy === "odds") {
          // SP-only race → fallback to runner number
          if (!winnerMarket) {
            return compare(a.number, b.number, sortDirection);
          }

          const aOutcome = Object.values(winnerMarket.outcomes).find(
            (m) => m.competitorId === a.id,
          );
          const bOutcome = Object.values(winnerMarket.outcomes).find(
            (m) => m.competitorId === b.id,
          );
          const aOdds = aOutcome?.odds || 0;
          const bOdds = bOutcome?.odds || 0;

          return compare(aOdds, bOdds, sortDirection);
        }

        return 0;
      });
  }, [competitors, sortDirection, sortBy, markets]);

  const validCompetitorsCount = (sortedCompetitors || []).filter(
    (c) => !c.scratched,
  ).length;

  const isSP = !(markets || []).some(
    (market) => market.marketType === "RACING_WINNER",
  );

  const hasFlucs = useMemo(() => {
    if (!winnerMarket) return false;

    return Object.values(winnerMarket.outcomes).every(
      (outcome) => Number(outcome.openingOdds) !== 1,
    );
  }, [winnerMarket]);

  const hasSpeedMap = useMemo(() => {
    if (!competitors) return false;

    return competitors
      .filter((c) => !c.scratched)
      .every(
        (c) => !!c.startingPosition && !!c.formAttributes.speedMapPosition,
      );
  }, [competitors]);

  const topFlucs = useMemo(() => {
    if (!winnerMarket || !competitors) return;

    return Object.entries(winnerMarket.outcomes)
      .filter(([_, outcome]) => {
        const competitor = competitors.find(
          (c) => c.id === outcome.competitorId,
        );
        return !competitor?.scratched;
      })
      .map(([outcomeId, outcome]) => {
        return [
          outcomeId,
          {
            ...outcome,
            fluc:
              100 / Number(outcome.odds) - 100 / Number(outcome.openingOdds),
          },
        ] as const;
      })
      .filter(([, outcome]) => outcome.fluc > 0)
      .sort(([, a], [, b]) => {
        return b.fluc - a.fluc;
      })
      .splice(0, 3)
      .map(([, outcome], index) => ({
        competitorId: outcome.competitorId,
        position: index + 1,
      }));
  }, [markets, winnerMarket, competitors]);

  const favouriteCompetitorId = useMemo(() => {
    if (!competitors || !winnerMarket) return undefined;

    return Object.values(winnerMarket.outcomes).reduce<string | undefined>(
      (currentFav, outcome) => {
        const competitor = competitors.find(
          (c) => c.id === outcome.competitorId,
        );
        if (!competitor || competitor.scratched) return currentFav;

        const odds = Number(outcome.odds);
        if (!odds || isNaN(odds)) return currentFav;

        if (!currentFav) return competitor.id;

        const currentOutcome = Object.values(winnerMarket.outcomes).find(
          (o) => o.competitorId === currentFav,
        );
        const currentOdds = Number(currentOutcome?.odds);
        if (!currentOdds || isNaN(currentOdds)) return competitor.id;

        return odds < currentOdds ? competitor.id : currentFav;
      },
      undefined,
    );
  }, [competitors, winnerMarket]);

  return {
    competitors: sortedCompetitors,
    markets,
    isLoading: competitorsLoading || marketsLoading,
    isSP,
    topFlucs,
    favouriteCompetitorId: isSP ? undefined : favouriteCompetitorId,
    hasFlucs,
    validCompetitorsCount,
    hasSpeedMap,
  } as const;
};

type ProcessedCompetitorMarket = Always<
  ReturnType<typeof useSortedCompetitors>["markets"]
>["0"];

type ProcessedCompetitor = Always<
  ReturnType<typeof useSortedCompetitors>["competitors"]
>["0"];

type TopFluc = Always<ReturnType<typeof useSortedCompetitors>["topFlucs"]>["0"];

export { useSortedCompetitors };
export type { ProcessedCompetitor, ProcessedCompetitorMarket, TopFluc };
